/**
 * Phone numbers.
 *
 * Numbers have been stored E.164 since June 2026, but the restored dumps are
 * full of rows written before that — `0905 123 456`, `00421905123456`,
 * `905123456`. Lookup and de-duplication therefore match on the **last nine
 * digits**, which is what the backend does and what makes an old row and a new
 * one recognise each other (FRONTEND.md §14.7, BACKEND.md §14.7).
 */

const SLOVAK_COUNTRY_CODE = '421'
const SIGNIFICANT_DIGITS = 9

export function digitsOf(phone: string | null | undefined): string {
  return (phone ?? '').replace(/\D/g, '')
}

// `lastNineDigits` and `phonesMatch` were here and are deleted — §9.35, 2026-08-06. They were this
// file's copy of the last-nine-digits rule, and nothing in either app ever called them: `phonesMatch`
// had no caller and `lastNineDigits` had exactly one, inside `phonesMatch`, which is why the list
// only ever reported the outer of the two.
//
// **Matching happens in the database, by design.** `Phones.MATCH_DIGITS = 9` and
// `Phones.matchClause` build `RIGHT(regexp_replace(...), 9) = :phone` into the lookup query, so an
// old row and a new one recognise each other in SQL — across every row, not across whatever page a
// screen happens to hold. No frontend surface compares two numbers; the ones that take a phone send
// it and let the server answer.
//
// A second implementation of a matching rule is worth removing rather than keeping available: it
// cannot be exercised, so it cannot be shown to still agree, and the day the backend's rule learns a
// second country code this copy would not. `toE164` stays — formatting a number for storage is a
// client job, and it has five callers.

/**
 * Best-effort E.164, defaulting to Slovakia.
 *
 * Returns null when there is not enough to work with, so a caller can keep the
 * raw value rather than persist a number it invented.
 */
export function toE164(
  phone: string | null | undefined,
  defaultCountryCode: string = SLOVAK_COUNTRY_CODE,
): string | null {
  const raw = (phone ?? '').trim()
  if (raw === '') return null

  const digits = digitsOf(raw)
  if (digits === '') return null

  if (raw.startsWith('+')) return `+${digits}`
  if (digits.startsWith('00')) return `+${digits.slice(2)}`
  // A national number: one trunk zero, then the subscriber digits.
  if (digits.startsWith('0')) return `+${defaultCountryCode}${digits.slice(1)}`
  if (digits.startsWith(defaultCountryCode)) return `+${digits}`
  if (digits.length === SIGNIFICANT_DIGITS) return `+${defaultCountryCode}${digits}`

  return `+${digits}`
}

/** `+421905123456` → `+421 905 123 456`. Falls back to the raw value it cannot group. */
export function formatPhone(phone: string | null | undefined): string {
  const e164 = toE164(phone)
  if (!e164) return ''

  const digits = e164.slice(1)
  if (digits.length < SIGNIFICANT_DIGITS) return e164

  const countryCode = digits.slice(0, digits.length - SIGNIFICANT_DIGITS)
  const subscriber = digits.slice(-SIGNIFICANT_DIGITS)
  const grouped = `${subscriber.slice(0, 3)} ${subscriber.slice(3, 6)} ${subscriber.slice(6)}`
  return countryCode ? `+${countryCode} ${grouped}` : `+${grouped}`
}
